import { useState, useCallback, useMemo, useRef } from "react";
import { GameData, GameDataItem } from "../models/GameData";
import { Highlighter, Typeahead, TypeaheadRef } from "react-bootstrap-typeahead";
import { GamePlayedItemUser, UserGamePlayedItem } from "../models/PlayData";

import '../playTracker.css';
import { orderBy } from "lodash";
import dayjs from "dayjs";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

export interface GameSearchProps {
    gameData: GameData | undefined;
    onFocus: () => void;
    onSubmit: (gameToAdd: GameDataItem, playedOnOverride?: Date) => void;
    recentPlays?: readonly UserGamePlayedItem[];
    currentUserId?: string;
}

interface GameSearchOption {
    gameId: string;
    name: string;
    existingPlayedOn?: Date;
    existingUsers?: GamePlayedItemUser[];
}

export const GameSearch = ({ gameData, onFocus, onSubmit, recentPlays, currentUserId }: GameSearchProps): React.ReactElement => {
    const ref = useRef<TypeaheadRef>(null);
    const [gameSelections, setGameSelections] = useState<string[]>([]);

    const addGame = useCallback((gameName: string, playedOnOverride?: Date): boolean => {
        const gameDataToAdd = gameData?.games.find((game) => game.name === gameName);
        if (gameDataToAdd) {
            onSubmit(gameDataToAdd, playedOnOverride);
            return true;
        }
        return false;
    }, [gameData?.games, onSubmit]);

    const gameSearchOptions: GameSearchOption[] = useMemo(() => {
        const playsWithoutMe = recentPlays
            ?.filter((play) => !play.users.some((user) => user.userId === currentUserId));
        const mostRecentEligibleOptions = orderBy(playsWithoutMe, 'playedOn', 'desc');
        const playOptions: GameSearchOption[] = mostRecentEligibleOptions
            .slice(0, 5)
            .map((play) => ({
                gameId: play.gameId,
                name: play.name,
                existingPlayedOn: play.playedOn,
                existingUsers: play.users,
            })) ?? [];
        const gameOptions: GameSearchOption[] = gameData?.games.map((game) => ({
                gameId: game.id,
                name: game.name,
            })) ?? [];
        return [...playOptions, ...gameOptions];
    }, [currentUserId, gameData?.games, recentPlays]);

    return (
        <Typeahead
            id='game-search'
            ref={ref}
            onChange={(selectedOptions: any[]) => {
                let games = selectedOptions as GameSearchOption[];
                if (games.length && addGame(games[0].name, games[0].existingPlayedOn)) {
                    setGameSelections([]);
                    ref.current?.blur();
                }
            }}
            onFocus={onFocus}
            labelKey='name'
            options={gameSearchOptions}
            selected={gameSelections}
            placeholder='Add a game...'
            renderMenuItemChildren={(option: any, { text }) => {
                const game = option as GameSearchOption;

                const UserIcon = ({ user }: { user: GamePlayedItemUser }) => (
                    <OverlayTrigger overlay={<Tooltip id={user.userId}>{user.userName || 'Unknown'}</Tooltip>}>
                        <span>{user.userIcon}</span>
                    </OverlayTrigger>
                );
                const users = game?.existingUsers?.map((user) => <span key={user.userId} style={{ float: 'right' }}><UserIcon user={user} /></span>)
                
                return (
                    <>
                        <Highlighter search={text}>{game.name}</Highlighter>
                        {game.existingPlayedOn && <div>
                            <small>{<em>{dayjs(game.existingPlayedOn).fromNow()}</em>} with {users}</small>
                        </div>}
                    </>
                )
            }}
        />
    )
}
